import { Crown, Check, Sparkles, ArrowRight, Mail, MessageSquare } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { PageShell } from './layout/PageShell';

interface ThankYouPageProps {
  subscriptionType: 'plus' | 'employer' | 'employer_plus';
  onContinue: () => void;
}

export function ThankYouPage({ subscriptionType, onContinue }: ThankYouPageProps) {
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIosMobile, setIsIosMobile] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(56);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof navigator === 'undefined') return;
    const iosPwa = document.documentElement.classList.contains('ios-pwa');
    const iosSafari = document.documentElement.classList.contains('ios') || /iPad|iPhone|iPod/.test(navigator.userAgent);
    const android = !iosPwa &&
      (document.documentElement.classList.contains('android-pwa') || /android/i.test(navigator.userAgent));
    setIsAndroid(!!android);
    setIsIosMobile(!!(iosPwa || iosSafari));
    if (android || iosPwa || iosSafari) {
      const el = document.querySelector('.pwa-nav-top');
      const h = el ? (el as HTMLElement).getBoundingClientRect().height : 56;
      setHeaderHeight(Math.round(h));
    }
  }, []);

  const contentByPlan: Record<
    ThankYouPageProps['subscriptionType'],
    {
      intro: string;
      benefitsTitle: string;
      benefits: string[];
      stepsTitle: string;
      steps: string[];
    }
  > = {
    plus: {
      intro: 'Du er nå Pluss-medlem hos Talentfunn.',
      benefitsTitle: 'Dine nye fordeler',
      benefits: [
        'Opptil 6 prosjekter i porteføljen',
        'Opptil 3 bilder per prosjekt',
        'Videopplasting på prosjekter',
        'Vis interesse for oppdrag',
        'Bedre synlighet i søkeresultater',
        'Pluss-badge på profilen',
      ],
      stepsTitle: 'Neste steg',
      steps: [
        'Gå til profilen din og oppdater porteføljen',
        'Legg inn bilder og video på relevante prosjekter',
        'Finn oppdrag og vis interesse med en kort pitch',
      ],
    },
    employer_plus: {
      intro: 'Du har nå Arbeidsgiver Pluss.',
      benefitsTitle: 'Dine nye muligheter',
      benefits: [
        'Ubegrensede meldinger til talenter',
        'Publiser oppdrag',
        'Vis interesse for oppdrag',
        'Favoritter og lagrede søk',
        'Varsling på relevante treff',
      ],
      stepsTitle: 'Kom i gang',
      steps: [
        'Publiser et oppdrag på Oppdrag-siden',
        'Søk etter talenter med riktig kompetanse',
        'Start dialog via meldinger',
      ],
    },
    employer: {
      intro: 'Du har nå Arbeidsgiver Basis.',
      benefitsTitle: 'Dine nye muligheter',
      benefits: [
        'Ubegrensede meldinger til talenter',
        'Kontakt talenter direkte fra profil',
        'Lagre favoritter',
        'Vis interesse for oppdrag',
      ],
      stepsTitle: 'Kom i gang',
      steps: [
        'Finn relevante talenter',
        'Start samtaler direkte fra profil',
        'Følg opp svar i Meldinger',
      ],
    },
  };

  const activeContent = contentByPlan[subscriptionType] ?? contentByPlan.employer;

  const contentInner = (
    <div className="max-w-xl mx-auto">
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-full blur-xl opacity-40" />
                <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-full p-4">
                  <Crown className="w-10 h-10 text-white" />
                </div>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl text-center mb-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-1">
              Takk for bestillingen!
            </h1>
            <p className="text-center text-gray-700 mb-6">{activeContent.intro}</p>

            <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 sm:p-5 mb-4 text-left">
              <h2 className="text-lg mb-3 text-purple-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {activeContent.benefitsTitle}
              </h2>
              <ul className="space-y-2.5">
                {activeContent.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 text-sm sm:text-base">{benefit}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-purple-50 rounded-2xl p-4 sm:p-5 mb-5 text-left border border-purple-100">
              <h3 className="text-lg mb-3 text-purple-900 flex items-center gap-2">
                {subscriptionType === 'plus' ? <ArrowRight className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
                {activeContent.stepsTitle}
              </h3>
              <ol className="space-y-2 text-gray-700 list-decimal list-inside text-sm sm:text-base">
                {activeContent.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>

            <div className="flex items-center justify-center gap-2 text-gray-600 mb-5">
              <Mail className="w-4 h-4" />
              <p className="text-sm">Du får en bekreftelse på e-post fra Nets/Nexi</p>
            </div>

            <button
              onClick={onContinue}
              className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white rounded-2xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] text-base sm:text-lg font-medium"
            >
              Fortsett til Talentfunn
            </button>

            <p className="text-sm text-gray-500 mt-4 text-center">
              Du kan når som helst administrere abonnementet under &quot;Abonnement&quot;.
            </p>

            <div className="text-center mt-5 text-gray-600 text-sm">
              Opplever du problemer? Kontakt oss på{' '}
              <a href="mailto:kontakt@talentfunn.no" className="text-purple-600 hover:underline">
                kontakt@talentfunn.no
              </a>
            </div>
  </div>
  );

  const useMobileScrollLayout = isAndroid || isIosMobile;

  return (
    <>
      {useMobileScrollLayout && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="xl:hidden fixed left-0 right-0 bottom-0 flex flex-col overflow-hidden bg-white z-[39]"
          style={{ top: headerHeight }}
        >
          <PageShell className="flex-1 min-h-0 overflow-y-auto ios-scroll px-4 sm:px-6 pt-5">
            {contentInner}
          </PageShell>
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className={`min-h-full max-h-screen overflow-hidden xl:max-h-none xl:overflow-visible bg-white xl:talentfunn-modal-overlay xl:backdrop-blur-md flex items-stretch xl:items-center justify-center xl:p-4 xl:pt-4 xl:pb-4 ${useMobileScrollLayout ? 'hidden xl:flex' : ''}`}
      >
        <motion.div
          initial={{ y: 20, opacity: 0.98 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full min-h-full h-full min-h-0 xl:min-h-0 xl:h-auto bg-white rounded-none xl:rounded-3xl max-w-none xl:max-w-2xl shadow-none xl:shadow-2xl flex flex-col"
        >
          <PageShell className="min-h-full h-full min-h-0 ios-scroll px-4 sm:px-6 pt-5 pt-page-safe-sm xl:pt-8 xl:pb-8">
            {contentInner}
          </PageShell>
        </motion.div>
      </motion.div>
    </>
  );
}
